import { useEffect, useMemo, useRef, useState } from 'react';
import { useC3Chart } from '../../hooks/useC3Chart';
import { splitSmallSegments, type LabelValue } from '../../utilities/chartData';
import { formatBytes } from '../../utilities/format';

interface Props {
  title: string;
  data: LabelValue[];
  valueType: 'bytes' | 'count';
  onItemClick?: (label: string) => void;
}

export function PieChartPanel({ title, data, valueType, onItemClick }: Props) {
  const chartRef = useRef<HTMLDivElement>(null);
  const onItemClickRef = useRef(onItemClick);
  onItemClickRef.current = onItemClick;

  // Clicking "Other" zooms into the slices it groups; undo returns to the full view.
  const [drilled, setDrilled] = useState(false);
  useEffect(() => setDrilled(false), [data]);
  const { main, small } = useMemo(() => splitSmallSegments(data), [data]);

  useC3Chart(chartRef, () => {
    if (!data.length) return null;
    const segments = drilled ? small : main;
    return {
      data: {
        columns: segments.map(d => [d.label, d.value]),
        type: 'pie',
        colors: { Other: '#c3c3c3' },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onclick: (d: any) => {
          if (!drilled && d.id === 'Other') setDrilled(true);
          else onItemClickRef.current?.(d.id);
        },
      },
      tooltip: {
        format: {
          value: (value: number) =>
            valueType === 'bytes' ? formatBytes(value) : value.toLocaleString(),
        },
      },
      interaction: { brighten: false },
    };
  }, [main, small, drilled, valueType]);

  return (
    <div className="col-md-5 text-center">
      <h2>{title}</h2>
      <div style={{ height: 36 }}>
        {drilled && (
          <i
            className="fa fa-undo"
            role="button"
            aria-label="Back to all segments"
            onClick={() => setDrilled(false)}
            style={{ fontSize: 24, cursor: 'pointer', opacity: 0.5 }}
          />
        )}
      </div>
      <div ref={chartRef} />
    </div>
  );
}
